import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../modules/angular.module';
import { ZorroModule } from '../../../modules/zorro.module';
import { HeaderComponent } from '../../ui/journey/header/header.component';
import { JourneyStep } from '../journey/journey.component';
import { NavigationService } from '../../../services/navigation.service';
import { Client, Course, Engines, Location, Product, Section, TokenPack } from '@mira-avant/mtlibs';
import { SectionService } from '../../../services/section.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { DisplayNamePipe } from '../../../pipes/display-name.pipe';
import { ReportingService } from '../../../services/reporting.service';
import { CourseService } from '../../../services/course.service';
import { LicenseService } from '../../../services/license';
import { LocationService } from '../../../services/location.service';
import { ClientService } from '../../../services/client.service';
import { StrideDistrictReport } from '../../ui/reports/stride/stride-district-report/stride-district-report';
import { StrideStudentReport } from '../../ui/reports/stride/stride-student-report/stride-student-report';
import { StrideSectionReport } from '../../ui/reports/stride/stride-section-report/stride-section-report';
import { ProductService } from '../../../services/product.service';
import { Products } from '../../ui/reports/products/products';
import { StrideSchoolReport } from '../../ui/reports/stride/stride-school-report/stride-school-report';
import { JourneyStudentReport } from '../../ui/reports/journey/journey-student-report/journey-student-report';
import { JourneySectionReport } from '../../ui/reports/journey/journey-section-report/journey-section-report';
import { JourneySchoolReport } from '../../ui/reports/journey/journey-school-report/journey-school-report';
import { JourneyDistrictReport } from '../../ui/reports/journey/journey-district-report/journey-district-report';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import { StudentSectionSelector } from './student-section-selector/student-section-selector';

export enum ReportState {
	STUDENT_SECTION_SELECTOR = 'student-section-selector',
	STRIDE_DISTRICT_REPORT = 'stride-district-report',
	STRIDE_SCHOOL_REPORT = 'stride-school-report',
	STRIDE_SECTION_REPORT = 'stride-section-report',
	STRIDE_STUDENT_REPORT = 'stride-student-report',
	JOURNEY_DISTRICT_REPORT = 'journey-district-report',
	JOURNEY_SCHOOL_REPORT = 'journey-school-report',
	JOURNEY_SECTION_REPORT = 'journey-section-report',
	JOURNEY_STUDENT_REPORT = 'journey-student-report',
}

@Component({
	selector: 'app-reports',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		HeaderComponent,
		DisplayNamePipe,
		Products,
		StrideStudentReport,
		StrideSectionReport,
		StrideSchoolReport,
		StrideDistrictReport,
		JourneyStudentReport,
		JourneySectionReport,
		JourneySchoolReport,
		JourneyDistrictReport,
		StudentSectionSelector,
	],
	templateUrl: './reports.component.html',
	styleUrl: './reports.component.scss',
})
export class ReportsComponent {
	public ReportState = ReportState;
	public products = signal<Product[]>([]);
	public Engines = Engines;
	public state = signal<ReportState | undefined>(undefined);
	public step = signal<JourneyStep>(JourneyStep.CHOOSE_COURSE_AND_UNIT_AND_LESSON);
	private sub?: Subscription;
	public loading = signal<boolean>(true);

	public hasStrideEngine = signal<boolean>(false);
	public hasJourneyEngine = signal<boolean>(false);
	public hasBothEngines = signal<boolean>(false);

	public selectedProduct = signal<Partial<Product> | undefined>(undefined);
	public selectedLocation = signal<Location | undefined>(undefined);
	public selectedCourse = signal<Course | undefined>(undefined);
	public selectedSection = signal<Section | undefined>(undefined);
	public selectedStudentUuid = signal<string | undefined>(undefined);
	public isHighestRoleStudent = signal<boolean>(false);

	constructor(
		private nav: NavigationService,
		private clientService: ClientService,
		private reportingService: ReportingService,
		private licenseService: LicenseService,
		private locationService: LocationService,
		private courseService: CourseService,
		private sectionService: SectionService,
		private productService: ProductService,
		private authService: AuthService,
		private userService: UserService
	) {}

	async ngOnInit() {
		const allProducts = await this.productService.getProducts$();
		this.products.set(allProducts);

		const activeProducts = await this.licenseService.getActiveProducts$();

		this.hasStrideEngine.set(activeProducts.some(product => product.engine_name === Engines.STRIDE));
		this.hasJourneyEngine.set(activeProducts.some(product => product.engine_name === Engines.JOURNEY));
		this.hasBothEngines.set(this.hasStrideEngine() && this.hasJourneyEngine());

		const client = await this.clientService.getClientBySubDomain$();

		if (!client) {
			console.error('No client found for subdomain');
			return;
		}

		this.sub = this.nav.matrixParams$.subscribe(async matrixParams => {
			await this.updateStateByUrl(client.uuid, activeProducts);
		});
	}

	private async updateStateByUrl(clientUuid: string, activeProducts: Product[]): Promise<void> {
		this.loading.set(true);

		try {
			const matrixParams = await firstValueFrom(this.nav.matrixParams$);

			const productName = matrixParams['product'];

			this.isHighestRoleStudent.set(await this.authService.isHighestRoleStudent$());

			if (productName && activeProducts.find(product => product.name === productName)) {
				this.selectedProduct.set(activeProducts.find(product => product.name === productName));
			} else {
				this.clearSelections();
			}

			const locationUuid = matrixParams['locationUuid'];
			const sectionUuid = matrixParams['sectionUuid'];
			const studentUuid = matrixParams['studentUuid'];

			await this.applyLocation(locationUuid);

			await this.applySection(sectionUuid);

			if (studentUuid) {
				this.selectedStudentUuid.set(studentUuid);
			} else if (sectionUuid) {
				this.selectedStudentUuid.set(undefined);
			} else {
				this.selectedStudentUuid.set(undefined);
				this.selectedSection.set(undefined);
				this.selectedCourse.set(undefined);
			}

			this.updateState();
		} catch (error) {
			console.error('Error processing matrix parameters:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private async applyLocation(locationUuid: string) {
		if (locationUuid && this.selectedLocation()?.uuid !== locationUuid) {
			const location = await this.locationService.get$(locationUuid);
			this.selectedLocation.set(location);
		} else if (!locationUuid) {
			this.selectedLocation.set(undefined);
		}
	}

	private async applySection(sectionUuid: string) {
		if (sectionUuid && this.selectedSection()?.uuid !== sectionUuid) {
			const section = await this.sectionService.get$(sectionUuid);
			this.selectedSection.set(section);

			const course = await this.courseService.get$(section.course_uuid);
			this.selectedCourse.set(course);
		} else if (!sectionUuid) {
			this.selectedSection.set(undefined);
			this.selectedCourse.set(undefined);
		}
	}

	private setStrideState(): void {
		if (this.selectedStudentUuid()) {
			this.state.set(ReportState.STRIDE_STUDENT_REPORT);
		} else if (this.selectedSection()) {
			this.state.set(ReportState.STRIDE_SECTION_REPORT);
		} else if (this.selectedLocation()) {
			this.state.set(ReportState.STRIDE_SCHOOL_REPORT);
		} else {
			this.state.set(ReportState.STRIDE_DISTRICT_REPORT);
		}
	}

	private setJourneyState(): void {
		if (this.selectedStudentUuid()) {
			this.state.set(ReportState.JOURNEY_STUDENT_REPORT);
		} else if (this.selectedSection()) {
			this.state.set(ReportState.JOURNEY_SECTION_REPORT);
		} else if (this.selectedLocation()) {
			this.state.set(ReportState.JOURNEY_SCHOOL_REPORT);
		} else {
			this.state.set(ReportState.JOURNEY_DISTRICT_REPORT);
		}
	}

	private updateState(): void {
		if (!this.selectedStudentUuid() && this.isHighestRoleStudent()) {
			this.state.set(ReportState.STUDENT_SECTION_SELECTOR);
		} else if (this.selectedProduct()?.engine_name === Engines.STRIDE) {
			this.setStrideState();
		} else if (this.selectedProduct()?.engine_name === Engines.JOURNEY) {
			this.setJourneyState();
		} else {
			this.state.set(undefined);
		}
	}

	public async onStudentSelectedSection(section: Section): Promise<void> {
		const tokenPack = this.authService.getCurrentTokenPack();

		if (!tokenPack) {
			throw new Error('No token pack found for current user');
		}

		const studentCognitoUuid = tokenPack.cognitoUser?.uuid;

		if (!studentCognitoUuid) {
			throw new Error('No cognito uuid found for current user');
		}

		const miraStudentUuid = await this.userService.convertCognitoUuidToUuid$(studentCognitoUuid);

		if (!miraStudentUuid) {
			throw new Error('No mira uuid found for current user');
		}

		const sectionUuid = section.uuid;
		const locationUuid = section.location_uuid;
		const selectedProduct = section.course?.product;

		if (!selectedProduct) {
			throw new Error('No product found for selected section');
		}

		this.clearAndNavigate(
			['/', 'my', 'reports'],
			{
				engine: selectedProduct.engine_name,
				product: selectedProduct.name,
				locationUuid,
				sectionUuid,
				studentUuid: miraStudentUuid,
			},
			false
		);
	}

	public selectProduct(product?: Partial<Product>): void {
		this.clearAndNavigate(
			['/', 'my', 'reports'],
			product?.engine_name && product?.name ? { engine: product.engine_name, product: product.name } : {},
			false
		);
	}

	public selectLocation(location?: Partial<Location>): void {
		const params: any = {};

		if (this.selectedProduct()?.engine_name && this.selectedProduct()?.name) {
			params.engine = this.selectedProduct()?.engine_name;
			params.product = this.selectedProduct()?.name;
		}

		if (location?.uuid) {
			params.locationUuid = location.uuid;
		} else {
			this.selectedLocation.set(undefined);
		}

		this.clearAndNavigate(['/', 'my', 'reports'], params, false);
	}

	public selectSection(section?: Section): void {
		const params: any = {};

		if (this.selectedProduct()?.engine_name && this.selectedProduct()?.name) {
			params.engine = this.selectedProduct()?.engine_name;
			params.product = this.selectedProduct()?.name;
		}

		if (this.selectedLocation()?.uuid) {
			params.locationUuid = this.selectedLocation()?.uuid;
		} else {
			this.selectedLocation.set(undefined);
		}

		if (section?.uuid) {
			params.sectionUuid = section.uuid;
		} else {
			this.selectedSection.set(undefined);
		}

		this.clearAndNavigate(['/', 'my', 'reports'], params, false);
	}

	public onUp(): void {
		this.clearAndNavigate(['/', 'my', 'reports']);
	}

	public navigateToDashboard(): void {
		this.clearAndNavigate(['/', 'my', 'reports']);
	}

	private clearAndNavigate(
		paths: string[],
		params: Record<string, string> = {},
		appendParams: boolean = false
	): void {
		this.clearSelections();
		this.nav.navigateWithMatrix$(paths, params, appendParams);
	}

	private clearSelections(): void {
		this.selectedProduct.set(undefined);
		this.selectedLocation.set(undefined);
		this.selectedCourse.set(undefined);
		this.selectedSection.set(undefined);
		this.selectedStudentUuid.set(undefined);
	}

	public onBackClick(): void {
		this.reportingService.navigateBack();
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}
}
